import { Box, Button, CircularProgress, Popover } from "@mui/material";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/sharedHooks";
import { NotificationsState } from "../models/notificationModels";
import {
  fetchNotifications,
  markAllAsRead,
  closePanel,
} from "../notificationsSlice";
import NotificationItem from "./NotificationItem";
import useTranslation from "../../hooks/useTranslation";

interface NotificationPanelProps {
  anchorEl: HTMLElement | null;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ anchorEl }) => {
  const { translate } = useTranslation(["common"]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { notifications, loading, isPanelOpen, unreadCount } =
    useAppSelector<NotificationsState>((state: any) => state.notifications);

  useEffect(() => {
    if (isPanelOpen) {
      dispatch(fetchNotifications());
    }
  }, [isPanelOpen, dispatch]);

  const handleMarkAllAsRead = () => {
    dispatch(markAllAsRead());
  };

  const handleClose = () => {
    dispatch(closePanel());
  };

  return (
    <Popover
      open={isPanelOpen && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          sx: {
            width: 380,
            maxHeight: 480,
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <p className="text-16-bold" style={{ color: "#333" }}>
          {translate("common:notifications.title")}
        </p>
        {unreadCount > 0 && (
          <Button
            size="small"
            onClick={handleMarkAllAsRead}
            sx={{ textTransform: "none", fontSize: "12px" }}
          >
            {translate("common:notifications.markAllAsRead")}
          </Button>
        )}
      </Box>

      <Box sx={{ overflowY: "auto", maxHeight: 400 }}>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: "32px",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}

        {!loading && notifications.length === 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "48px 16px",
            }}
          >
            <p className="text-14-regular" style={{ color: "#9ca3af" }}>
              {translate("common:notifications.empty")}
            </p>
          </Box>
        )}

        {!loading &&
          notifications.slice(0, 4).map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
            />
          ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          padding: "8px 16px",
          borderTop: "1px solid #e5e7eb",
        }}
      >
        <Button
          size="small"
          onClick={() => {
            dispatch(closePanel());
            navigate("/notifications");
          }}
          sx={{ textTransform: "none", fontSize: "12px" }}
        >
          {translate("common:notifications.viewAll")}
        </Button>
      </Box>
    </Popover>
  );
};

export default NotificationPanel;
